<template>
  <div>
    <Head :title="__('Reset Password')" />

    <form
      @submit.prevent="attempt"
      class="bg-white dark:bg-gray-800 shadow rounded-lg p-8 py-12 max-w-md mx-auto"
    >
      <h2 class="text-2xl text-center font-bold mb-6">
        {{ __('Welcome!') }}
      </h2>

      <div class="mb-6">
        <input
          v-model="form.code"
          id="code"
          type="hidden"
          name="code"
        />

        <HelpText class="mt-2 text-red-500" v-if="form.errors.has('code')">
          {{ form.errors.first('code') }}
        </HelpText>
      </div>

      <div class="mb-6">
        <label class="block mb-2" for="password">{{ __('New Password') }}</label>
        <div class="relative">
          <input
            v-model="form.password"
            class="form-control form-input form-control-bordered w-full pr-10"
            :class="{
              'form-control-bordered-error': form.errors.has('password'),
            }"
            id="password"
            :type="show ? 'text' : 'password'"
            name="password"
            required=""
          />
          <!-- Show-hide password -->
          <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 cursor-pointer">
            <svg class="w-5 h-5" @click="show = !show" :class="{'hidden': show, 'block': !show }" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="#bdbdbd" viewBox="0 0 24 24">
              <path d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
            </svg>
            <svg class="w-5 h-5" @click="show = !show" :class="{'hidden': !show, 'block': show }" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="#bdbdbd" viewBox="0 0 24 24">
              <path d="M0 0h24v24H0zm0 0h24v24H0zm0 0h24v24H0zm0 0h24v24H0z" fill="none"/>
              <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
            </svg>
          </div>
          <!-- End show-hide password -->
        </div>

        <HelpText class="mt-2 text-red-500" v-if="form.errors.has('password')">
          {{ form.errors.first('password') }}
        </HelpText>
      </div>

      <div class="mb-6">
        <label class="block mb-2" for="password_confirmation">{{
          __('Confirm New Password')
        }}</label>
        <div class="relative">
          <input
            v-model="form.password_confirmation"
            class="form-control form-input form-control-bordered w-full pr-10"
            :class="{
              'form-control-bordered-error': form.errors.has(
                'password_confirmation'
              ),
            }"
            id="password_confirmation"
            :type="showConfirm ? 'text' : 'password'"
            name="password_confirmation"
            required=""
          />
          <!-- Show-hide password -->
          <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 cursor-pointer">
            <svg class="w-5 h-5" @click="showConfirm = !showConfirm" :class="{'hidden': showConfirm, 'block': !showConfirm }" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="#bdbdbd" viewBox="0 0 24 24">
              <path d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
            </svg>
            <svg class="w-5 h-5" @click="showConfirm = !showConfirm" :class="{'hidden': !showConfirm, 'block': showConfirm }" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="#bdbdbd" viewBox="0 0 24 24">
              <path d="M0 0h24v24H0zm0 0h24v24H0zm0 0h24v24H0zm0 0h24v24H0z" fill="none"/>
              <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
            </svg>
          </div>
          <!-- End show-hide password -->
        </div>

        <HelpText
          class="mt-2 text-red-500"
          v-if="form.errors.has('password_confirmation')"
        >
          {{ form.errors.first('password_confirmation') }}
        </HelpText>
      </div>

      <Button
        class="w-full flex justify-center"
        type="submit"
        :loading="form.processing"
      >
        {{ __('Log In') }}
      </Button>
    </form>
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import Auth from '@/layouts/Auth'
import { Button } from 'laravel-nova-ui'

export default {
  layout: Auth,

  components: {
    Button,
  },

  props: ['code', 'token'],

  data() {
    return {
      show: false,
      showConfirm: false,
      form: Nova.form({
        code: this.code,
        password: '',
        password_confirmation: '',
        token: this.token,
      }),
    }
  },

  methods: {
    async attempt() {
      try {
        const { message } = await this.form.post(Nova.url('/password/reset'))
        const redirect = { url: Nova.url('/'), remote: true }

        Cookies.set('token', Math.random().toString(36), { expires: 365 })

        Nova.success(message)
        Nova.visit(redirect)
      } catch (error) {
        if (error.response?.status === 400) {
          return Nova.error(error?.response?.data?.message)
        }

        if (error.response?.status === 500) {
          return Nova.error(this.__('There was a problem submitting the form.'))
        }
      }
    },
  },
}
</script>
